import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { CryptoIcon, ExchangeIcon } from './IconDisplay';
import SignedNumberField from './SignedNumberField';
import { formatWithCommas, removeCommas } from '../utils/dateUtils';

const DEFAULT_FORM = {
  trading_pair: '',
  trade_mode: 'capital_krw',
  trade_capital: '',
  min_order_capital: '',
  max_order_capital: '',
  allowed_slippage: '0.1',
  grid_low: '',
  grid_high: '',
  gap: '',
  count: '5',
  duplicate_policy: 'skip',
  status: 'stopped',
  skip_low: false,
  skip_high: false,
  position_mode: 'bidirectional',
  allowed_direction: 'ls_to_sl',
  remark: '',
};

const parseNumber = (value) => {
  const normalized = removeCommas(String(value ?? '')).trim();
  if (normalized === '') {
    return null;
  }
  const number = Number(normalized);
  return Number.isFinite(number) ? number : null;
};

const roundHalfUp = (value, decimals = 3) => {
  const factor = 10 ** decimals;
  if (value >= 0) {
    return Math.floor((value * factor) + 0.5) / factor;
  }
  return Math.ceil((value * factor) - 0.5) / factor;
};

const formatTriggerDecimal = (value) => roundHalfUp(value, 3).toFixed(3);

const calculateEstimatedRows = ({ gridLow, gridHigh, gap, count }) => {
  if (
    gridLow === null ||
    gridHigh === null ||
    gap === null ||
    !Number.isInteger(count) ||
    count < 1 ||
    count > 300 ||
    gridLow >= gridHigh ||
    gap <= 0 ||
    gap > gridHigh - gridLow
  ) {
    return { rows: [], step: null };
  }

  if (count > 1 && (gridHigh - gridLow - gap) / (count - 1) < 0.001) {
    return { rows: [], step: null };
  }

  const step = count === 1 ? null : (gridHigh - gridLow - gap) / (count - 1);
  const rows = Array.from({ length: count }, (_, index) => {
    if (count === 1) {
      const low = roundHalfUp(gridLow + ((gridHigh - gridLow - gap) / 2), 3);
      return {
        index: index + 1,
        low: formatTriggerDecimal(low),
        high: formatTriggerDecimal(low + gap),
      };
    }

    if (index === count - 1) {
      const high = roundHalfUp(gridHigh, 3);
      return {
        index: index + 1,
        low: formatTriggerDecimal(high - gap),
        high: formatTriggerDecimal(high),
      };
    }

    const low = roundHalfUp(gridLow + (step * index), 3);
    return {
      index: index + 1,
      low: formatTriggerDecimal(low),
      high: formatTriggerDecimal(low + gap),
    };
  });

  return { rows, step };
};

const getApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) {
    return error?.message || 'Request failed';
  }
  if (typeof data === 'string') {
    return data;
  }
  if (data.error) {
    return data.error;
  }
  if (data.detail) {
    return data.detail;
  }
  return JSON.stringify(data);
};

function TriggerGridCreateDialog({
  open,
  onClose,
  tradingPairs,
  onPreview,
  onCreate,
  onCreated,
  showSuccess,
  showError,
  confirm,
}) {
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  const selectedPair = useMemo(
    () => tradingPairs.find(pair => pair.id === formData.trading_pair),
    [tradingPairs, formData.trading_pair]
  );

  const isFuturesVsFutures = selectedPair &&
    selectedPair.target_market_type !== 'SPOT' &&
    selectedPair.origin_market_type !== 'SPOT';

  const estimatedGrid = useMemo(() => calculateEstimatedRows({
    gridLow: parseNumber(formData.grid_low),
    gridHigh: parseNumber(formData.grid_high),
    gap: parseNumber(formData.gap),
    count: Number(formData.count),
  }), [formData.grid_low, formData.grid_high, formData.gap, formData.count]);

  const validationErrors = useMemo(() => {
    const errors = {};
    const capital = parseNumber(formData.trade_capital);
    const minCapital = parseNumber(formData.min_order_capital);
    const maxCapital = parseNumber(formData.max_order_capital);
    const allowedSlippage = parseNumber(formData.allowed_slippage);
    const gridLow = parseNumber(formData.grid_low);
    const gridHigh = parseNumber(formData.grid_high);
    const gap = parseNumber(formData.gap);
    const count = Number(formData.count);

    if (!formData.trading_pair) {
      errors.trading_pair = 'Required';
    }
    if (!capital || capital <= 0) {
      errors.trade_capital = 'Required';
    }
    if (minCapital && maxCapital && minCapital > maxCapital) {
      errors.min_order_capital = 'Min must be <= max';
    }
    if (maxCapital && capital && maxCapital > capital) {
      errors.max_order_capital = 'Max must be <= trade capital';
    }
    if (!allowedSlippage || allowedSlippage <= 0) {
      errors.allowed_slippage = 'Required';
    }
    if (gridLow === null) {
      errors.grid_low = 'Required';
    }
    if (gridHigh === null) {
      errors.grid_high = 'Required';
    }
    if (gridLow !== null && gridHigh !== null && gridLow >= gridHigh) {
      errors.grid_high = 'Must be greater than grid low';
    }
    if (!gap || gap <= 0) {
      errors.gap = 'Required';
    }
    if (gridLow !== null && gridHigh !== null && gap && gap > gridHigh - gridLow) {
      errors.gap = 'Must fit inside grid range';
    }
    if (!Number.isInteger(count) || count < 1 || count > 300) {
      errors.count = '1-300';
    }
    if (
      Number.isInteger(count) &&
      count > 1 &&
      gridLow !== null &&
      gridHigh !== null &&
      gap &&
      (gridHigh - gridLow - gap) / (count - 1) < 0.001
    ) {
      errors.count = 'Too dense for 0.001 precision';
    }

    return errors;
  }, [formData]);

  const hasErrors = Object.keys(validationErrors).length > 0;

  const resetAndClose = () => {
    setFormData(DEFAULT_FORM);
    setPreview(null);
    setPreviewLoading(false);
    setCreateLoading(false);
    onClose();
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    setPreview(null);
  };

  const handleCapitalChange = (field) => (event) => {
    const rawValue = removeCommas(event.target.value);
    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
      setFormData(prev => ({ ...prev, [field]: rawValue }));
      setPreview(null);
    }
  };

  const buildPayload = (dryRun) => {
    const payload = {
      dry_run: dryRun,
      trading_pair: formData.trading_pair,
      currency: formData.trade_mode === 'capital_usdt' ? 'USDT' : 'KRW',
      trade_capital: Number(removeCommas(formData.trade_capital)),
      allowed_slippage: Number(formData.allowed_slippage) / 100,
      grid_low: formData.grid_low,
      grid_high: formData.grid_high,
      gap: formData.gap,
      count: Number(formData.count),
      duplicate_policy: formData.duplicate_policy,
      status: formData.status,
      skip_low: formData.skip_low,
      skip_high: formData.skip_high,
      position_mode: isFuturesVsFutures ? formData.position_mode : 'bidirectional',
      allowed_direction: formData.allowed_direction,
      remark: formData.remark,
    };

    if (formData.min_order_capital) {
      payload.min_order_capital = Number(removeCommas(formData.min_order_capital));
    }
    if (formData.max_order_capital) {
      payload.max_order_capital = Number(removeCommas(formData.max_order_capital));
    }

    return payload;
  };

  const handlePreview = async () => {
    if (hasErrors) return;
    setPreviewLoading(true);
    try {
      const result = await onPreview(buildPayload(true));
      setPreview(result);
    } catch (error) {
      showError(getApiErrorMessage(error));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreate = async () => {
    if (hasErrors || !preview || preview.create_count <= 0) return;

    if (formData.status === 'active') {
      const ok = await confirm({
        title: 'Create active grid?',
        message: `${preview.create_count} triggers will be active immediately after creation.`,
        confirmLabel: 'Create Active',
        severity: 'warning',
      });
      if (!ok) return;
    }

    setCreateLoading(true);
    try {
      const result = await onCreate(buildPayload(false));
      showSuccess(`Created ${result.created_count} trigger${result.created_count === 1 ? '' : 's'}`);
      onCreated();
      resetAndClose();
    } catch (error) {
      showError(getApiErrorMessage(error));
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle>Grid Create Triggers</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            select
            fullWidth
            label="Trading Pair"
            value={formData.trading_pair}
            onChange={handleChange('trading_pair')}
            margin="normal"
            required
            error={!!validationErrors.trading_pair}
            helperText={validationErrors.trading_pair}
          >
            {tradingPairs
              .filter(pair => pair.is_active)
              .map((pair) => (
                <MenuItem key={pair.id} value={pair.id}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CryptoIcon symbol={pair.target_symbol || pair.symbol} size={20} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {pair.symbol}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5 }}>
                        <ExchangeIcon exchangeName={pair.target_exchange_name} size={14} />
                        <Typography variant="caption" color="textSecondary">
                          Target: {pair.target_exchange_name} - {pair.target_market_type} ({pair.target_quote_asset})
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ExchangeIcon exchangeName={pair.origin_exchange_name} size={14} />
                        <Typography variant="caption" color="textSecondary">
                          Origin: {pair.origin_exchange_name} - {pair.origin_market_type} ({pair.origin_quote_asset})
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
          </TextField>

          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Trade Mode
            </Typography>
            <RadioGroup row value={formData.trade_mode} onChange={handleChange('trade_mode')}>
              <FormControlLabel value="capital_krw" control={<Radio />} label="KRW" />
              <FormControlLabel value="capital_usdt" control={<Radio />} label="USDT" />
            </RadioGroup>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={formData.trade_mode === 'capital_krw' ? 'Trade Capital (KRW)' : 'Trade Capital (USDT)'}
                value={formatWithCommas(formData.trade_capital)}
                onChange={handleCapitalChange('trade_capital')}
                margin="normal"
                error={!!validationErrors.trade_capital}
                helperText={validationErrors.trade_capital}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={formData.trade_mode === 'capital_krw' ? 'Min Order Capital (KRW)' : 'Min Order Capital (USDT)'}
                value={formatWithCommas(formData.min_order_capital)}
                onChange={handleCapitalChange('min_order_capital')}
                margin="normal"
                error={!!validationErrors.min_order_capital}
                helperText={validationErrors.min_order_capital}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={formData.trade_mode === 'capital_krw' ? 'Max Order Capital (KRW)' : 'Max Order Capital (USDT)'}
                value={formatWithCommas(formData.max_order_capital)}
                onChange={handleCapitalChange('max_order_capital')}
                margin="normal"
                error={!!validationErrors.max_order_capital}
                helperText={validationErrors.max_order_capital}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <SignedNumberField
                fullWidth
                label="Grid Low (%)"
                value={formData.grid_low}
                onChange={handleChange('grid_low')}
                margin="normal"
                error={!!validationErrors.grid_low}
                helperText={validationErrors.grid_low}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SignedNumberField
                fullWidth
                label="Grid High (%)"
                value={formData.grid_high}
                onChange={handleChange('grid_high')}
                margin="normal"
                error={!!validationErrors.grid_high}
                helperText={validationErrors.grid_high}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Gap (%)"
                type="number"
                value={formData.gap}
                onChange={handleChange('gap')}
                margin="normal"
                error={!!validationErrors.gap}
                helperText={validationErrors.gap}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Count"
                type="number"
                value={formData.count}
                onChange={handleChange('count')}
                margin="normal"
                error={!!validationErrors.count}
                helperText={validationErrors.count}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Allowed Slippage (%)"
                type="number"
                value={formData.allowed_slippage}
                onChange={handleChange('allowed_slippage')}
                margin="normal"
                error={!!validationErrors.allowed_slippage}
                helperText={validationErrors.allowed_slippage}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Status"
                value={formData.status}
                onChange={handleChange('status')}
                margin="normal"
              >
                <MenuItem value="stopped">Stopped</MenuItem>
                <MenuItem value="active">Active</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Duplicates"
                value={formData.duplicate_policy}
                onChange={handleChange('duplicate_policy')}
                margin="normal"
              >
                <MenuItem value="skip">Skip</MenuItem>
                <MenuItem value="error">Error</MenuItem>
                <MenuItem value="allow">Allow</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Switch checked={formData.skip_low} onChange={handleChange('skip_low')} />}
              label="Skip Low"
            />
            <FormControlLabel
              control={<Switch checked={formData.skip_high} onChange={handleChange('skip_high')} />}
              label="Skip High"
            />
          </Box>

          {isFuturesVsFutures && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                Position Mode
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <TextField
                  select
                  label="Position Mode"
                  value={formData.position_mode}
                  onChange={handleChange('position_mode')}
                  size="small"
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="bidirectional">Bidirectional</MenuItem>
                  <MenuItem value="unidirectional">Unidirectional</MenuItem>
                </TextField>
                {formData.position_mode === 'unidirectional' && (
                  <TextField
                    select
                    label="Allowed Direction"
                    value={formData.allowed_direction}
                    onChange={handleChange('allowed_direction')}
                    size="small"
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="ls_to_sl">LS -&gt; SL</MenuItem>
                    <MenuItem value="sl_to_ls">SL -&gt; LS</MenuItem>
                  </TextField>
                )}
              </Box>
            </Box>
          )}

          <TextField
            fullWidth
            label="Remark"
            value={formData.remark}
            onChange={handleChange('remark')}
            margin="normal"
            multiline
            rows={2}
          />

          {formData.status === 'active' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Active grid triggers can fire immediately after creation.
            </Alert>
          )}

          {estimatedGrid.rows.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  Estimated Triggers
                </Typography>
                <Chip size="small" label={`${estimatedGrid.rows.length} rows`} />
                {estimatedGrid.step !== null && (
                  <Chip size="small" label={`Step ${formatTriggerDecimal(estimatedGrid.step)}%`} />
                )}
              </Box>
              <TableContainer sx={{ maxHeight: 220, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Low</TableCell>
                      <TableCell>High</TableCell>
                      <TableCell>Gap</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {estimatedGrid.rows.map((row) => (
                      <TableRow key={`estimate-${row.index}-${row.low}-${row.high}`}>
                        <TableCell>{row.index}</TableCell>
                        <TableCell>{row.low}%</TableCell>
                        <TableCell>{row.high}%</TableCell>
                        <TableCell>{formatTriggerDecimal(Number(row.high) - Number(row.low))}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Preview checks existing duplicates before creation.
              </Typography>
            </Box>
          )}

          {preview && (
            <Box sx={{ mt: 3 }}>
              <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                <Chip size="small" label={`Generated ${preview.total_generated}`} />
                <Chip size="small" color="success" label={`Create ${preview.create_count}`} />
                <Chip size="small" color={preview.duplicate_count ? 'warning' : 'default'} label={`Duplicates ${preview.duplicate_count}`} />
              </Box>
              {preview.error && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  {preview.error}
                </Alert>
              )}
              <TableContainer sx={{ maxHeight: 260, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Low</TableCell>
                      <TableCell>High</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.rows.map((row) => (
                      <TableRow key={`${row.index}-${row.low}-${row.high}`}>
                        <TableCell>{row.index}</TableCell>
                        <TableCell>{row.low}%</TableCell>
                        <TableCell>{row.high}%</TableCell>
                        <TableCell>
                          {row.will_create ? (
                            <Chip size="small" color="success" label="Create" />
                          ) : (
                            <Chip size="small" color="warning" label={row.reason || 'Skip'} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-start', px: 3 }}>
        <Button onClick={resetAndClose} disabled={previewLoading || createLoading}>
          Cancel
        </Button>
        <Button
          onClick={handlePreview}
          variant="outlined"
          disabled={hasErrors || previewLoading || createLoading}
        >
          {previewLoading ? 'Previewing...' : 'Preview'}
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={hasErrors || !preview || preview.create_count <= 0 || previewLoading || createLoading}
        >
          {createLoading ? 'Creating...' : 'Create Grid'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TriggerGridCreateDialog;
